import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { HighPriorityIndicator } from "@/Common/Components/IconControls/HighPriorityIndicator";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { CommunicationStateType, CommunicationsUIStateInbox, CommunicationTasksSortOrderType, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ISearchMessageRequest } from "@/Communications/Api/Dto/Requests/ISearchMessageRequest";
import { MessageOrTaskSearchResponseModel } from "@/Communications/Api/Dto/Responses/MessageOrTaskSearchResponseModel";
import { MessageOrTaskItem } from "@/Communications/Models/MessageOrTaskItem";
import { MessageOrTasksTableItem } from "@/Communications/Models/MessageOrTaskTableItems";
import { CommunicationsTab_OptimizedV2 } from "../CommunicationsTab_OptimizedV2";
import { TaskSettingsContainer_OptimizedV2, TaskSettingsContainer_OptimizedV2State } from "./TaskSettingsContainer_OptimizedV2";

interface ITaskSearch_OptimizedV2Props {
    inboxType: CommunicationsUIStateInbox;
    shouldReload?: boolean;

    onLoadSuccess?: () => void;
    showNotesPopup?: (item: MessageOrTasksTableItem, e: React.MouseEvent) => void;
    selectedTaskRowClick?: (item: MessageOrTaskItem) => void;
    communicationsCount?: () => void;

    savedState?: TaskSearch_OptimizedV2State;
    settingSavedState?: TaskSettingsContainer_OptimizedV2State;
}

class TaskSearch_OptimizedV2State {
    public tasks: MessageOrTasksTableItem[];
    public searchSettings: TaskSettingsContainer_OptimizedV2State;
    public isSearchSettingsOpen: boolean;

    public isSelectAllChecked: boolean;
    public searchText: string;
    public currentPage: number;
    public totalQueryPages: number;
    public totalQueryRows: number;
    public selectedPageNumber: number;

    public sortingColumn: CommunicationTasksSortOrderType;
    public sortingDirection: SortDirection;
    public isColumnOrderingPopupOpen: boolean;

    public static getDefault(): TaskSearch_OptimizedV2State {
        const s = new TaskSearch_OptimizedV2State();

        s.isSelectAllChecked = false;
        s.tasks = [];
        s.searchText = "";
        s.currentPage = 1;
        s.selectedPageNumber = 1;
        s.totalQueryPages = 0;
        s.totalQueryRows = 0;
        s.searchSettings = TaskSettingsContainer_OptimizedV2State.getDefault();

        return s;
    }
}

class TaskSearch_OptimizedV2 extends BaseReactComponent<ITaskSearch_OptimizedV2Props, TaskSearch_OptimizedV2State> {
    private _saveOptionKey: string;
    private _viewName = cacheStore.GetString("InboxLabel");

    public constructor(props: ITaskSearch_OptimizedV2Props) {
        super(props);

        this.state = props.savedState || TaskSearch_OptimizedV2State.getDefault();
    }

    public componentDidMount(): void {
        this.loadTasks(this.props.inboxType);

        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: ITaskSearch_OptimizedV2Props): void {
        const inboxTypeChanged = this.props.inboxType !== prevProps.inboxType;
        const shouldReloadToggled = this.props.shouldReload && !prevProps.shouldReload;

        if (inboxTypeChanged || shouldReloadToggled) {
            this.loadTasks(this.props.inboxType);
        }
    }

    public componentWillUnmount(): void {
        setTimeout(() => sessionStore.dispatch(
            LocalOperations.Communications.CommunicationStatesSave,
            {
                type: CommunicationStateType.TaskSearch,
                state: this.state
            }
        ), 0);
    }

    public render(): JSX.Element {
        switch (this.props.inboxType) {
            case CommunicationsUIStateInbox.Inbox:
                this._viewName = cacheStore.GetString("InboxLabel");
                this._saveOptionKey = "CommunicationTASKSGrid_INBOX";
                break;

            case CommunicationsUIStateInbox.Sent:
                this._viewName = cacheStore.GetString("SentFLabel");
                this._saveOptionKey = "CommunicationTASKSGrid_SENT";
                break;

            case CommunicationsUIStateInbox.Drafts:
                this._viewName = cacheStore.GetString("DraftsLabel");
                this._saveOptionKey = "CommunicationTASKSGrid_DRAFTS";
                break;

            case CommunicationsUIStateInbox.Archive:
                this._viewName = cacheStore.GetString("ArchivedTasksLabel");
                this._saveOptionKey = "CommunicationTASKSGrid_ARCHIVE";
                break;

            case CommunicationsUIStateInbox.Trash:
                this._viewName = cacheStore.GetString("TrashLabel");
                this._saveOptionKey = "CommunicationTASKSGrid_TRASH";
                break;
        }

        const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(this._saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const tableColumns = this.getTableColumns(columnsOptions);
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const columnConfiguration = cacheStore.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        const shouldShowDeleteButton = (
            this.props.inboxType !== CommunicationsUIStateInbox.Sent &&
            this.props.inboxType !== CommunicationsUIStateInbox.Trash
        );
        const shouldShowArchiveButton = this.props.inboxType === CommunicationsUIStateInbox.Inbox;
        const shouldShowRestoreButton = this.props.inboxType === CommunicationsUIStateInbox.Trash;
        const shouldShowMarkAsUnreadButton = this.props.inboxType === CommunicationsUIStateInbox.Inbox;

        const nbSelectedItems = this.state.tasks.filter(x => x.IsSelected).length;
        const nbReadSelectedItems = this.state.tasks.filter(x => x.IsSelected && x.Model.IsRead).length;

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={`${cacheStore.GetString("TasksLabel")} : ${this._viewName}`}
                    searchText={this.state.searchText}
                    searchPlaceholder={cacheStore.GetString("SearchInTasksLabel")}
                    onSearch={text => this.mutateState(s => {
                        s.searchText = text;
                        s.selectedPageNumber = 1;
                        s.currentPage = 1;
                    }, () => this.loadTasks(this.props.inboxType))}
                    onRefreshClick={() => this.loadTasks(this.props.inboxType)}
                    onSettingsClick={() => this.mutateState(s => s.isSearchSettingsOpen = !this.state.isSearchSettingsOpen)}
                />
                <div id="posref" className="tb ws-scrollable">
                    {this.state.isSearchSettingsOpen && (
                        <TaskSettingsContainer_OptimizedV2
                            enableSaveSetting={this.props.inboxType == CommunicationsUIStateInbox.Inbox}
                            onSaveSettingsCallback={containerSettings => {
                                this.mutateState(
                                    s => {
                                        s.searchSettings = containerSettings;
                                        s.isSearchSettingsOpen = false;
                                        s.selectedPageNumber = 1;
                                    },
                                    () => this.loadTasks(this.props.inboxType)
                                );
                            }}
                            onCloseCallback={() => this.mutateState(s => s.isSearchSettingsOpen = false)}
                            onSettingsButtonClick={() => {
                                this.mutateState(s => {
                                    s.isColumnOrderingPopupOpen = true;
                                    s.isSearchSettingsOpen = false;
                                });
                            }}
                            savedState={() => communicationsStore.communicationsTabSavedState.taskSettingContainerSavedState}
                        />
                    )}

                    <div className="myle-doc-tb main-content myle-form">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="communications_message_table"
                            TableDataCy="communication-table"
                            Columns={tableColumns}
                            DataSource={this.state.tasks.toImmutable()}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            SaveOptionskeyTarget={this._saveOptionKey}
                            RowClickFunction={e => {
                                if (this.state.isSearchSettingsOpen) {
                                    this.mutateState(s => s.isSearchSettingsOpen = false);
                                    return;
                                }

                                this.props.selectedTaskRowClick(e.Model);
                            }}
                            ColumnReorderTarget={MyleTableTarget.Myle_CommunicationsMessages_Search_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_CommunicationsMessages_Search_ColumnSortClick}
                        />
                    </div>
                </div>
                <div className={CommunicationsTab_OptimizedV2.footerCSS}>
                    <div className="col-lg-6 col-md-6 col-sm-6">
                        <SimplePagerControl
                            PageSize={this.state.searchSettings.selectedPageSizeOption}
                            TotalRows={this.state.totalQueryRows}
                            CurrentPage={this.state.currentPage}
                            changePageFunction={(pageNumber: number) => {
                                this.mutateState(s => s.selectedPageNumber = pageNumber, () => this.loadTasks(this.props.inboxType));
                            }}
                        />
                    </div>
                    <div className="col-lg-6 col-md-6 col-sm-6 text-right">
                        {shouldShowDeleteButton && (
                            <button
                                data-cy="taskSearch-delete"
                                disabled={nbSelectedItems <= 0}
                                className="btn btn-danger push-right-5"
                                onClick={() => this.onDeleteClick()}
                            >
                                {cacheStore.GetString("DeleteLabel")}
                            </button>
                        )}
                        {shouldShowArchiveButton && (
                            <button
                                data-cy="taskSearch-archive"
                                disabled={nbSelectedItems <= 0}
                                className="btn btn-primary push-right-5"
                                onClick={() => this.onArchiveClick()}
                            >
                                {cacheStore.GetString("ArchiveButtonLabel")}
                            </button>
                        )}
                        {shouldShowRestoreButton && (
                            <button
                                data-cy="taskSearch-restore"
                                disabled={nbSelectedItems <= 0}
                                className="btn btn-primary"
                                onClick={() => this.onRestoreClick()}
                            >
                                {cacheStore.GetString("RestoreLabel")}
                            </button>
                        )}
                        {shouldShowMarkAsUnreadButton && (
                            <button
                                disabled={nbReadSelectedItems <= 0}
                                className="btn btn-default push-right-5"
                                onClick={() => this.onMarkAsUnreadClick()}
                            >
                                {cacheStore.GetString("MarkAsUnreadLabel")}
                            </button>
                        )}
                    </div>
                </div>
                {this.state.isColumnOrderingPopupOpen && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={columnConfiguration}
                        SaveOptionskeyTarget={this._saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        closeColumnOrderPopup={() =>
                            this.mutateState(s => s.isColumnOrderingPopupOpen = false)
                        }
                    />
                )}
            </div>
        );
    }

    public closeSettingsContainer(): void {
        if (!this.state.isSearchSettingsOpen)
            return;
        this.mutateState(
            s => {
                s.isSearchSettingsOpen = false;
                s.selectedPageNumber = 1;
            },
            () => this.loadTasks(this.props.inboxType)
        );
    }

    private loadTasks(inboxType: CommunicationsUIStateInbox): void {
        let notStartedStatusId = this.getStatusByCode("notstarted");
        let deferredStatusId = this.getStatusByCode("deferred");
        let inProgressStatusId = this.getStatusByCode("inprogress");
        let waitingStatusId = this.getStatusByCode("waiting");
        let completedStatusId = this.getStatusByCode("completed");

        let searchSettings = this.state.searchSettings;
        let requestData: ISearchMessageRequest = {
            PracticianIds: searchSettings.clinicians ? searchSettings.clinicians.filter(x => x.Checked).map(x => x.Id) : [],
            SharedWithIds: searchSettings.sharedWith.filter(x => x.Checked).map(x => x.Id),
            PriorityIds: searchSettings.priorities.filter(x => x.Checked).map(x => x.Id),
            SelectedStatusIds: searchSettings.statuses.filter(x => x.Checked).map(x => x.Id),
            NotStartedStatusId: notStartedStatusId.LookupID,
            DeferredStatusId: deferredStatusId.LookupID,
            InProgressStatusId: inProgressStatusId.LookupID,
            WaitingStatusId: waitingStatusId.LookupID,
            CompletedStatusId: completedStatusId.LookupID,
            FromDueDate: searchSettings.dateFrom,
            ToDueDate: searchSettings.dateTo,
            PageNumber: this.state.selectedPageNumber,
            PageSize: searchSettings.selectedPageSizeOption,
            SearchString: this.state.searchText,
            IsTask: true,
            OrderByPriorityFirst: searchSettings.orderByPriorityFirst
        };

        switch (inboxType) {
            case CommunicationsUIStateInbox.Drafts:
                delete requestData.PracticianIds;
                delete requestData.IsArchive;
                requestData.IsTrash = false;
                requestData.IsDraft = true;
                requestData.IsSent = true;
                requestData.UIState = CommunicationsUIStateInbox.Drafts;
                break;

            case CommunicationsUIStateInbox.Sent:
                delete requestData.IsTrash;
                delete requestData.IsArchive;
                requestData.IsDraft = false;
                requestData.IsSent = true;
                requestData.UIState = CommunicationsUIStateInbox.Sent;
                break;

            case CommunicationsUIStateInbox.Trash:
                requestData.IsTrash = true;
                delete requestData.IsArchive;
                requestData.IsDraft = false;
                requestData.IsSent = false;
                requestData.UIState = CommunicationsUIStateInbox.Trash;
                break;

            case CommunicationsUIStateInbox.Archive:
                requestData.IsTrash = false;
                requestData.IsArchive = true;
                delete requestData.IsDraft;
                requestData.IsSent = false;
                requestData.UIState = CommunicationsUIStateInbox.Archive;
                break;

            case CommunicationsUIStateInbox.Inbox:
            default:
                requestData.IsTrash = false;
                requestData.IsArchive = false;
                requestData.IsDraft = false;
                requestData.IsSent = false;
                requestData.UIState = CommunicationsUIStateInbox.Inbox;
                break;
        }

        Client.Postv2(
            ServerOperations.Server.Myle_SearchMessagesOrTasks,
            requestData,
            response => {
                if (response) {
                    const model: MessageOrTaskSearchResponseModel = response;

                    this.mutateState(s => {
                        s.tasks = model.Messages.map(item => {
                            item.IsTask = true;
                            return new MessageOrTasksTableItem(item);
                        });
                        s.currentPage = model.CurrentPage;
                        s.totalQueryPages = model.TotalQueryPages;
                        s.totalQueryRows = model.TotalQueryRows;

                        s.isSelectAllChecked = false;
                    }, () => this.props.onLoadSuccess && this.props.onLoadSuccess());
                }
            }
        );
    }

    public getStatusByCode(code: string): FlatLookupView {
        const statusLookups = cacheStore.FindLookupsByTypeCode("TaskStatus");
        return statusLookups.filter(l => l.LookupCode.toLowerCase() === code).firstOrDefault();
    }

    private onDeleteClick(): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                isDraftOwner: this.props.inboxType == CommunicationsUIStateInbox.Drafts,
                individualMessageIds: this.state.tasks.filter(x => x.IsSelected).map(x => x.Model.IM_ID),
                isTrash: true
            },
            response => {
                if (response.Success) {
                    this.loadTasks(this.props.inboxType);
                    this.props.communicationsCount && this.props.communicationsCount();
                }
            }
        );
    }

    private onArchiveClick(): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                isDraftOwner: this.props.inboxType == CommunicationsUIStateInbox.Drafts,
                individualMessageIds: this.state.tasks.filter(x => x.IsSelected).map(x => x.Model.IM_ID),
                isArchive: true
            },
            response => {
                if (response.Success) {
                    this.loadTasks(this.props.inboxType);
                    this.props.communicationsCount && this.props.communicationsCount();
                }
            }
        );
    }

    private onRestoreClick(): void {
        Client.Postv2(
            ServerOperations.Server.Myle_SetMessageState,
            {
                isDraftOwner: this.props.inboxType == CommunicationsUIStateInbox.Drafts,
                individualMessageIds: this.state.tasks.filter(x => x.IsSelected).map(x => x.Model.IM_ID),
                isArchive: false,
                isTrash: false
            },
            response => {
                if (response.Success) {
                    this.loadTasks(this.props.inboxType);
                    this.props.communicationsCount && this.props.communicationsCount();
                }
            }
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: "",
                FieldName: "Checkbox",
                WidthPixels: 68,
                IsFixedInPlace: true
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SubjectLabel"),
                FieldName: "Subject",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.Subject,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.Subject)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("FromLabel"),
                FieldName: "From",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.From,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.From)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ToLabel"),
                FieldName: "To",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.To,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.To)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("MessageDateLabel"),
                FieldName: "SendDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.MessageDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.MessageDate)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DueDateLabel"),
                FieldName: "DueDate",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.DueDate,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.DueDate)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusDLabel"),
                FieldName: "Status",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.Status,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.Status)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("lblDSQPriority"),
                FieldName: "Priority",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.Priority,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.Priority)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("PatientLabel"),
                FieldName: "Patient",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: CommunicationTasksSortOrderType.Patient,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10,
                sortingMethod: () => this.sortArrayBy(CommunicationTasksSortOrderType.Patient)
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionLabel"),
                FieldName: "Action",
                RowClickEnabled: false,
                IsSortable: false,
                IsFixedInPlace: true,
                columnClass: "align-center",
                WidthPixels: 80
            })
        ];
    }

    private sortArrayBy(column: CommunicationTasksSortOrderType): void {
        this.mutateState(s => {
            s.sortingColumn = column;
            s.sortingDirection = MyleTableHelper.getNewSortDir(column, s.sortingColumn, s.sortingDirection);
            s.tasks = s.tasks
                .sortByProperty(
                    task => {
                        let x = task.Model;

                        switch (column) {
                            case CommunicationTasksSortOrderType.Subject:
                                return (x.Subject || "").toUpperCase();

                            case CommunicationTasksSortOrderType.MessageDate:
                                return x.SendDate;

                            case CommunicationTasksSortOrderType.DueDate:
                                return x.DueDate ?? "";

                            case CommunicationTasksSortOrderType.From:
                                return x.FromContact && cacheStore.GetCliniciansPersonsByContactId(x.FromContact.Id).Name.toUpperCase();

                            case CommunicationTasksSortOrderType.To:
                                return x.ToContact && cacheStore.GetCliniciansPersonsByContactId(x.ToContact[0].Id).Name.toUpperCase();

                            case CommunicationTasksSortOrderType.Priority:
                                return cacheStore.GetLocalizedValueByLookupId(x.PriorityLookupId).toUpperCase();

                            case CommunicationTasksSortOrderType.Status:
                                return cacheStore.GetLocalizedValueByLookupId(x.TaskStatusLookupId).toUpperCase();

                            case CommunicationTasksSortOrderType.Patient:
                                return x.PatientLinks && x.PatientLinks.length > 0 ? x.PatientLinks[0].LastName.toUpperCase() : "";

                            default:
                                return x.DueDate ?? "";
                        }
                    },
                    s.sortingDirection === SortDirection.DESC
                );
        });
    }

    private getTableColumns(columnOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MessageOrTasksTableItem>(
            columnOptions,
            column => {
                switch (column.FieldName) {
                    case "Checkbox":
                        return this.buildCheckboxColumn();

                    case "Subject":
                        return this.buildSubjectColumn();

                    case "From":
                        return this.buildFromColumn();

                    case "To":
                        return this.buildToColumn();

                    case "SendDate":
                        return this.buildMessageDateColumn();

                    case "DueDate":
                        return this.buildDueDateColumn();

                    case "Status":
                        return this.buildStatusColumn();

                    case "Priority":
                        return this.buildPriorityColumn();

                    case "Patient":
                        return this.buildPatientColumn();

                    case "Action":
                        return this.buildActionColumn();
                }
            },
            false
        );
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private buildCheckboxColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                const completedLookupId = cacheStore.FindLookupByLookupCode("Completed", "TaskStatus").LookupID;
                const isCompleted = item.Model.GenericTaskStatusLookupId === completedLookupId;
                const isVisible = cacheStore.IsHighPriority(item.Model.PriorityLookupId) && !isCompleted ? "visible" : "hidden";

                if (item.Model.IsActionPossible) {
                    return (
                        <div>
                            <input
                                type="checkbox"
                                id={item.Model.Id}
                                onChange={() => this.setSelectedTask(item)}
                                checked={item.IsSelected}
                            />
                            <HighPriorityIndicator
                                imgStyle={{ verticalAlign: "baseline" }}
                                style={{
                                    marginLeft: "15px",
                                    visibility: isVisible
                                }}
                            />
                        </div>
                    );
                }
                else {
                    return (
                        <HighPriorityIndicator
                            imgStyle={{ verticalAlign: "baseline" }}
                            style={{
                                marginLeft: "28px",
                                visibility: isVisible
                            }}
                        />
                    );
                }
            },
            renderHeader: () => {
                if (this.props.inboxType !== CommunicationsUIStateInbox.Sent) {
                    return (
                        <div>
                            <input
                                type="checkbox"
                                onChange={() => this.setSelectedToAllTasks()}
                                checked={this.state.isSelectAllChecked}
                            />
                        </div>
                    );
                }
                else {
                    return (<div />);
                }
            }
        };
    }

    private buildSubjectColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => this.renderRowCommon(item.Model.Subject, item)
        };
    }

    private buildFromColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => this.renderRowCommon(item.Model.FromContact.LastName + " " + item.Model.FromContact.FirstName, item)
        };
    }

    private buildToColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                let rowColor = this.getRowColor(item);
                let toContacts = item.Model.ToContact;
                let arrToContact: JSX.Element[] = [];

                if (toContacts.length > 2) {
                    arrToContact = [<div key="toContact" title="recipients">{toContacts.length + " " + cacheStore.GetString("RecipientLabel") + "s"}</div>];
                }
                else {
                    arrToContact = toContacts.map((val, idx) => {
                        if (item.Model.IsRead) {
                            return (
                                <div
                                    style={{ color: rowColor }}
                                    key={`toContact_${idx}`}
                                    title={val.LastName + ", " + val.FirstName}
                                >
                                    {val.LastName + ", " + val.FirstName}
                                </div>
                            );
                        }
                        else {
                            return (
                                <div
                                    style={{ fontWeight: "bold", color: rowColor }}
                                    key={`toContact_${idx}`}
                                    title={val.LastName + ", " + val.FirstName}
                                >
                                    {val.LastName + ", " + val.FirstName}
                                </div>
                            );
                        }
                    });
                }

                if (item.Model.IsRead)
                    return (<div style={{ color: rowColor }}>{arrToContact} </div>);
                else
                    return (<div style={{ fontWeight: "bold", color: rowColor }}>{arrToContact} </div>);
            }
        };
    }

    private buildMessageDateColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                const dateTime = item.Model.StartDate ? item.Model.StartDate : item.Model.SendDate;
                const date = moment(dateTime).format("YYYY/MM/DD");
                const time = moment(dateTime).format("HH:mm:ss A");
                const creationDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;

                return this.renderRowCommon(creationDate, item);
            }
        };
    }

    private buildDueDateColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                let date = "";
                if (item.Model.DueDate != null)
                    date = moment(item.Model.DueDate).format("YYYY/MM/DD");

                let time = "";
                if (item.Model.DueDate != null)
                    time = moment(item.Model.DueDate).format("HH:mm:ss A");

                let dueDate = date + " " + cacheStore.GetString("AtLabel") + " " + time;
                if (date === "" && time === "")
                    dueDate = "";

                return this.renderRowCommon(dueDate, item);
            }
        };
    }

    private buildStatusColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                let statusLookupId = item.Model.GenericTaskStatusLookupId;

                let status = "";
                if (statusLookupId != null) {
                    const sl = cacheStore.GetLookupById(statusLookupId);
                    if (sl) { status = sl.GetValue(); }
                }

                return this.renderRowCommon(status, item);
            }
        };
    }

    private buildPriorityColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                let priorityLookupId = item.Model.PriorityLookupId;

                let priority = "";
                if (priorityLookupId != null) {
                    const pr = cacheStore.GetLookupById(priorityLookupId);
                    if (pr) { priority = pr.GetValue(); }
                }

                return this.renderRowCommon(priority, item);
            }
        };
    }

    private buildPatientColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: item => {
                const patients = item.Model.PatientLinks;
                const patientsArray = patients.map((val, idx) => {
                    const patientName = NameHelper.formatFullNameForHasName(val, NameHelperFormat.FirstNameFirst);
                    let patientElement = (
                        <div
                            key={`patient_${idx}`}
                            title={patientName}
                        >
                            <HyperLinkRedirectControl
                                target={RedirectTo.Patient}
                                redirectValue={val.Id}
                                text={patientName}
                            />
                        </div>
                    );
                    return patientElement;
                });
                return (<div>{patientsArray} </div>);
            }
        };
    }

    private buildActionColumn(): MyleTableColumnInit<MessageOrTasksTableItem> {
        return {
            renderCell: (item: MessageOrTasksTableItem) => {
                if (item.Model.TotalNotes > 0) {
                    return (
                        <a onClick={e => this.props.showNotesPopup(item, e)}>[ <span className="red-text">{item.Model.TotalNotes}</span> ]</a>
                    );
                }
                else {
                    return (
                        <a onClick={e => this.props.showNotesPopup(item, e)}>[ <span>+</span> ]</a>
                    );
                }
            }
        };
    }

    private renderRowCommon(text: string, item: MessageOrTasksTableItem): JSX.Element {
        let rowColor = this.getRowColor(item);
        if (item.Model.IsRead) {
            return (
                <div style={{ color: rowColor }}>{text}</div>
            );
        }
        else {
            return (
                <div style={{ fontWeight: "bold", color: rowColor }}>{text}</div>
            );
        }
    }

    private setSelectedTask(task: MessageOrTasksTableItem): void {
        this.mutateState(s => {
            for (let c of s.tasks) {
                if (c.Model.IMS_ID === task.Model.IMS_ID)
                    c.IsSelected = !c.IsSelected;
            }
        });
    }

    private setSelectedToAllTasks(): void {
        this.mutateState(s => {
            for (let c of s.tasks)
                c.IsSelected = !s.isSelectAllChecked;
            s.isSelectAllChecked = !s.isSelectAllChecked;
        });
    }

    private getRowColor(data: MessageOrTasksTableItem): string {
        let result = "";

        if (data.Model.GenericTaskStatusLookupId) {
            const status = cacheStore.GetLookupById(data.Model.GenericTaskStatusLookupId);
            if (status) {
                switch (status.LookupCode.toLowerCase()) {
                    case "completed": result = "green"; break;
                    case "inprogress": result = "blue"; break;
                    case "waiting": result = "#800080"; break;
                    case "deferred": result = "#AC2A2A"; break;
                    default: break;
                }
            }
            if (status.LookupCode.toLowerCase() !== "completed" && data.Model.DueDate) {
                if (moment() > moment(data.Model.DueDate)) {
                    result = "red";
                }
            }
        }
        return result;
    }

    private onMarkAsUnreadClick(): void {
        const messageIds = this.state.tasks.filter(m => m.IsSelected).map(m => m.Model.IM_ID);

        Client.Postv2(
            ServerOperations.Server.Myle_MarkMessagesAsUnRead,
            {
                MessageIds: messageIds
            },
            response => {
                if (response) {
                    this.loadTasks(this.props.inboxType);
                    this.props.communicationsCount && this.props.communicationsCount();
                }
            }
        );
    }
}

export { TaskSearch_OptimizedV2, TaskSearch_OptimizedV2State };
